import { useMemo, useState } from "react";
import { BellRing, CheckCircle2, HeartPulse, Inbox, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AionEmptyState } from "@/components/aion/AionEmptyState";
import { AionSectionCard } from "@/components/aion/AionSectionCard";

const FILTERS = [
  { id: "all", label: "Alle" },
  { id: "unread", label: "Ungelesen" },
  { id: "read", label: "Gelesen" },
];

const formatNotificationTime = (value) => {
  if (!value) return "Zeitpunkt nicht verfügbar";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
};

const priorityLabel = (priority) => ({
  critical: "Kritisch",
  warning: "Wichtig",
  info: "Info",
}[String(priority || "info").toLowerCase()] || "Info");

export const NotificationsView = ({
  activeProfile,
  notifications = [],
  onArchiveNotification,
  onMarkNotificationRead,
  onOpenDecisions,
  onOpenPersonalHealth,
  onOpenProjects,
}) => {
  const [filter, setFilter] = useState("all");
  const readOnly = activeProfile?.access_mode === "shared_view";

  const counts = useMemo(() => {
    const unread = notifications.filter((item) => !item?.read).length;
    return { all: notifications.length, unread, read: notifications.length - unread };
  }, [notifications]);

  const filtered = useMemo(() => {
    if (filter === "unread") return notifications.filter((item) => !item?.read);
    if (filter === "read") return notifications.filter((item) => item?.read);
    return notifications;
  }, [filter, notifications]);

  return (
    <div className="aion-view aion-view-stack" data-testid="notifications-view">
      <AionSectionCard
        title="Notifications Center"
        eyebrow="HINWEISE & UPDATES"
        subtitle="Alle AION-Hinweise an einem Ort. Nur echte Intelligence-Notifications aus deinem aktuellen Profil."
        testId="notifications-hero-card"
      >
        <div className="aion-status-badge" data-testid="notifications-profile-badge">
          <span>Profil: {activeProfile?.name || "Ich"}</span>
          <small>{readOnly ? "Freigegebene Hinweise · nur lesen" : "Privater Notification-Bereich"}</small>
        </div>
        <div className="aion-inline-stats" data-testid="notifications-counts">
          <div className="aion-inline-stat"><span>Gesamt</span><strong>{counts.all}</strong></div>
          <div className="aion-inline-stat"><span>Ungelesen</span><strong>{counts.unread}</strong></div>
          <div className="aion-inline-stat"><span>Gelesen</span><strong>{counts.read}</strong></div>
        </div>
        <div className="aion-inline-actions" data-testid="notifications-filter-actions">
          {FILTERS.map((item) => (
            <Button
              key={item.id}
              type="button"
              size="sm"
              variant={filter === item.id ? "default" : "outline"}
              onClick={() => setFilter(item.id)}
              data-testid={`notifications-filter-${item.id}`}
            >
              {item.label} · {counts[item.id]}
            </Button>
          ))}
        </div>
      </AionSectionCard>

      <AionSectionCard
        title="Hinweise"
        eyebrow="INBOX"
        subtitle="Gelesene Hinweise bleiben sichtbar, bis du sie archivierst."
        testId="notifications-list-card"
      >
        {filtered.length ? (
          <div className="aion-list" data-testid="notifications-list">
            {filtered.map((item, index) => {
              const priority = String(item?.priority || "info").toLowerCase();
              const PriorityIcon = priority === "critical" ? ShieldAlert : priority === "warning" ? BellRing : CheckCircle2;
              return (
                <div
                  className="aion-list-row aion-list-row-actions"
                  key={item.id || index}
                  data-testid={`notification-item-${index}`}
                >
                  <PriorityIcon className="aion-list-icon" aria-hidden="true" />
                  <div className="aion-list-copy">
                    <strong>{item.title || "AION Hinweis"}</strong>
                    <span>{item.message || "Keine Nachricht hinterlegt."}</span>
                    <small>
                      {priorityLabel(priority)} · {item.category || "general"} · {formatNotificationTime(item.created_at)}
                      {item.read ? " · Gelesen" : " · Neu"}
                    </small>
                  </div>
                  <div className="aion-inline-actions" data-testid={`notification-actions-${index}`}>
                    {item.payload?.action === "OPEN_PERSONAL_HEALTH" && onOpenPersonalHealth ? (
                      <Button size="sm" onClick={() => onOpenPersonalHealth()} data-testid={`notification-open-health-${index}`}>
                        <HeartPulse className="aion-button-icon" aria-hidden="true" />
                        Health öffnen
                      </Button>
                    ) : null}
                    {item.category === "decisions" && onOpenDecisions ? (
                      <Button size="sm" variant="outline" onClick={() => onOpenDecisions()} data-testid={`notification-open-decisions-${index}`}>
                        Decision Review
                      </Button>
                    ) : null}
                    {item.category === "planning" && item.payload?.planning_kind === "project" && onOpenProjects ? (
                      <Button size="sm" variant="outline" onClick={() => onOpenProjects(item.payload?.planning_id)} data-testid={`notification-open-project-${index}`}>
                        Projekt öffnen
                      </Button>
                    ) : null}
                    {!readOnly && !item.read ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onMarkNotificationRead?.(item.id)}
                        data-testid={`notification-read-${index}`}
                      >
                        Gelesen
                      </Button>
                    ) : null}
                    {!readOnly ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onArchiveNotification?.(item.id)}
                        data-testid={`notification-archive-${index}`}
                      >
                        Archivieren
                      </Button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <AionEmptyState
            icon={filter === "unread" ? Inbox : BellRing}
            title={filter === "unread" ? "Keine ungelesenen Hinweise." : filter === "read" ? "Keine gelesenen Hinweise." : "Keine Hinweise."}
            description="AION zeigt hier Hinweise aus Intelligence, Planning, Memory, Health und Systemanalysen."
            testId="notifications-empty"
          />
        )}
      </AionSectionCard>
    </div>
  );
};
